import React from 'react';
import { ProcessStepControl } from '../../services/ProcessStepService';
import { Shield, Brain, User, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

interface ControlItemProps {
  control: ProcessStepControl;
}

export const ControlItem: React.FC<ControlItemProps> = ({ control }) => {
  const getEffectivenessIcon = (rating: string | undefined) => {
    switch (rating) {
      case 'Effective':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Needs Improvement':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'Ineffective':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Shield className="h-4 w-4 text-gray-500" />;
    }
  };

  const getEffectivenessColor = (rating: string | undefined) => {
    switch (rating) {
      case 'Effective':
        return 'bg-green-100 text-green-800';
      case 'Needs Improvement':
        return 'bg-yellow-100 text-yellow-800';
      case 'Ineffective':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-3 border border-gray-200 rounded">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="font-medium text-[#57C8F4] text-sm">
            {control.control_id || `CTRL-${control.id}`}
          </div>
          {control.ai_recommended && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#57C8F4] text-white text-xs rounded">
              <Brain className="h-3 w-3" />
              AI
            </span>
          )}
          {!control.ai_recommended && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#9D7AB8] text-white text-xs rounded">
              <User className="h-3 w-3" />
              Manual
            </span>
          )}
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEffectivenessColor(control.control_effectiveness)}`}>
          {control.control_effectiveness || 'Not Rated'}
        </span>
      </div>

      <div className="text-sm font-medium text-[#221551] mb-1">
        {control.control_name}
      </div>

      <div className="text-xs text-gray-600 mb-2 line-clamp-2">
        {control.control_description}
      </div>

      {/* Regulatory Citations */}
      {control.regulatory_citations && Array.isArray(control.regulatory_citations) && control.regulatory_citations.length > 0 && (
        <div className="bg-gray-50 border-l-3 border-[#9D7AB8] p-2 mb-2">
          {control.regulatory_citations.map((citation: any, index: number) => (
            <div key={index} className="text-xs text-[#454F83] mb-1 last:mb-0">
              <strong>{citation.regulation}:</strong> {citation.reference}
              {citation.description && <span className="block text-gray-600">{citation.description}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 text-xs bg-gray-50 p-2 rounded">
        <div className="flex flex-col">
          <span className="font-medium text-[#454F83] mb-0.5">Control Type</span>
          <span className="text-[#221551]">{control.control_type}</span>
        </div>
        <div className="flex flex-col">
          <span className="font-medium text-[#454F83] mb-0.5">Frequency</span>
          <span className="text-[#221551]">{control.control_frequency || 'Not Specified'}</span>
        </div>
        <div className="flex flex-col">
          <span className="font-medium text-[#454F83] mb-0.5">Owner</span>
          <span className="text-[#221551]">{control.control_owner || 'Unassigned'}</span>
        </div>
        <div className="flex flex-col">
          <span className="font-medium text-[#454F83] mb-0.5">Last Tested</span>
          <span className="text-[#221551]">
            {control.last_tested_date 
              ? new Date(control.last_tested_date).toLocaleDateString()
              : 'Not Tested'
            }
          </span>
        </div>
      </div>

      {control.ai_confidence_score && (
        <div className="text-xs text-gray-500 mt-2">
          AI Confidence: {control.ai_confidence_score}%
        </div>
      )}

      {control.implementation_status && control.implementation_status !== 'implemented' && (
        <div className="mt-2 text-xs">
          <span className="font-medium text-[#454F83]">Status:</span>
          <span className={`ml-1 px-2 py-0.5 rounded ${
            control.implementation_status === 'planned' ? 'bg-blue-100 text-blue-800' :
            control.implementation_status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {control.implementation_status.replace('_', ' ')}
          </span>
        </div>
      )}
    </div>
  );
};